import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { fetchUsersByIdsBatched, User } from './userBatchFetcher'

const getUsersByIdQueryKey = (userId: string) => ['getUserById_normalised', userId]
// User identity data effectively never changes mid-session, and every refetch is a lambda invocation
const usersByIdStaleTime = 60 * 60 * 1000 // 1 hour

const isStale = (dataUpdatedAt: number | undefined) =>
  dataUpdatedAt != null ? Date.now() - dataUpdatedAt > usersByIdStaleTime : true

export const useSetUsersByIdCache = () => {
  const queryClient = useQueryClient()

  return (users: User[]) => {
    users.forEach((user) => {
      queryClient.setQueryData<User>(getUsersByIdQueryKey(user.id), user)
    })
  }
}

export const usersByIdQueryKey = 'getUsersByIds'

export const useGetUsersByIds = <TData = User[]>({
  userIds,
  select,
}: {
  userIds: string[]
  select?: (users: User[]) => TData
}) => {
  const { getToken } = useAuthenticatedUser()

  const queryClient = useQueryClient()
  const setUsersByIdCache = useSetUsersByIdCache()

  return useQuery({
    queryKey: [usersByIdQueryKey, userIds.slice().sort()], // Sort for consistent cache key
    queryFn: async () => {
      const userIdsToFetch = []
      const usersById: Record<string, User> = {}

      for (const userId of userIds) {
        const queryState = queryClient.getQueryState<User | null>(getUsersByIdQueryKey(userId))
        // A fresh null entry is a negatively-cached id (backend omitted it) and counts as known.
        if (queryState != null && !isStale(queryState.dataUpdatedAt)) {
          if (queryState.data) {
            usersById[userId] = queryState.data
          }
        } else {
          // If data is missing or stale, we must fetch it.
          userIdsToFetch.push(userId)
          // We can also add the stale data to our result map for now.
          if (queryState?.data) {
            usersById[userId] = queryState.data
          }
        }
      }

      // If we need to do some fetching, let's do it
      if (userIdsToFetch.length > 0) {
        const fetchedUsers = await fetchUsersByIdsBatched(userIdsToFetch, getToken)

        setUsersByIdCache(fetchedUsers)
        fetchedUsers.forEach((user) => {
          usersById[user.id] = user
        })

        // Cache omitted ids as null (known-absent) and drop any stale copy we were tentatively holding.
        const foundIds = new Set(fetchedUsers.map((user) => user.id))
        userIdsToFetch
          .filter((userId) => !foundIds.has(userId))
          .forEach((userId) => {
            queryClient.setQueryData<User | null>(getUsersByIdQueryKey(userId), null)
            delete usersById[userId]
          })
      }

      // Merge results from cache and fetched data
      return userIds.map((id) => usersById[id]).filter((user) => user != null)
    },
    // If we have all of the users in the cache already,
    // we can return users before firing the queryFn (which may need us to refetch the stale ones)
    placeholderData: (previousData) => {
      const users: User[] = []
      let allKnown = true
      for (const userId of userIds) {
        const cached = queryClient.getQueryData<User | null>(getUsersByIdQueryKey(userId))
        // undefined = never cached; null = known-absent, so dropped users don't force a loading flash.
        if (cached === undefined) {
          allKnown = false
        } else if (cached) {
          users.push(cached)
        }
      }
      if (allKnown) return users
      // While the new set loads, keep previously loaded users that are still requested (so a growing list
      // doesn't flicker to empty), but never users that dropped out of the set. With nothing to carry over,
      // return undefined so the query shows a genuine loading state rather than an empty success.
      const carriedOver = previousData?.filter((user) => userIds.includes(user.id)) ?? []
      return carriedOver.length > 0 ? carriedOver : undefined
    },
    select,
    enabled: userIds.length > 0,
    // Deliberately no staleTime: per-user freshness lives in the queryFn, so remounts re-read the per-user cache.
    gcTime: 1000 * 60 * 5, // 5 minutes - we'll keep the normalised data around for longer anyway (30 mins - default GC time)
  })
}

// Module-level so react-query sees a stable select and only re-derives the map when the data changes.
const usersToMapById = (users: User[]) => new Map(users.map((user) => [user.id, user]))

// Returns the same users keyed by id. Most consumers want to look a user up by id rather than iterate an
// array; this shares useGetUsersByIds' query/cache. Derived via `select` so callers are only re-rendered
// when the map matters, and `data` is undefined (not an empty map) while the query is pending.
export const useGetUsersByIdsMap = ({ userIds }: { userIds: string[] }) => useGetUsersByIds({ userIds, select: usersToMapById })

// This hook is useful for when you only need a single user by ID
// Shares the same normalised cache with useGetUsersByIds
export const useGetSingleUserById = (userId: string | null | undefined) => {
  const { getToken } = useAuthenticatedUser()
  return useQuery({
    queryKey: getUsersByIdQueryKey(userId as string),
    queryFn: async () => {
      if (userId == null) {
        // Should never happen - enabled will be false
        throw new Error('userId is required')
      }
      const users = await fetchUsersByIdsBatched([userId], getToken)
      return users[0] ?? null
    },
    staleTime: usersByIdStaleTime,
    enabled: userId != null,
  })
}
